import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ArrowClockwise, ArrowCounterClockwise, Eye, EyeSlash, Crosshair, Polygon, DownloadSimple, Ruler, Square, Lock, LockOpen, ArrowsOut, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STAGE_W = 900;
const STAGE_H = 600;
const DEF_TARGET = { a: [288, 300], b: [612, 300] };
const KINDS = [
  { v: "satellite", label: "Google / Satellite" },
  { v: "khasra", label: "Khasra / Cadastral" },
  { v: "masterplan", label: "Master Plan" },
  { v: "other", label: "Other Map" },
];

export const gisObjectUrl = (path) => `${API}/gis/object?path=${encodeURIComponent(path)}`;

function similarity(aSrc, bSrc, aDst, bDst) {
  const sx = bSrc[0] - aSrc[0], sy = bSrc[1] - aSrc[1];
  const dx = bDst[0] - aDst[0], dy = bDst[1] - aDst[1];
  const L = sx * sx + sy * sy;
  if (!L) return null;
  const re = (dx * sx + dy * sy) / L;
  const im = (dy * sx - dx * sy) / L;
  const tx = aDst[0] - (re * aSrc[0] - im * aSrc[1]);
  const ty = aDst[1] - (im * aSrc[0] + re * aSrc[1]);
  return { a: re, b: im, c: -im, d: re, e: tx, f: ty };
}

function niceNum(x) {
  if (!(x > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(x)));
  const f = x / p;
  const n = f < 1.5 ? 1 : f < 3.5 ? 2 : f < 7.5 ? 5 : 10;
  return n * p;
}
function fmtM(m) {
  return m >= 1000 ? `${(m / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} km`
    : `${m.toLocaleString(undefined, { maximumFractionDigits: m < 10 ? 1 : 0 })} m`;
}

function snapRightAngle(prevPrev, prev, pt) {
  let rx, ry;
  if (prevPrev) { rx = prev[0] - prevPrev[0]; ry = prev[1] - prevPrev[1]; }
  else { rx = 1; ry = 0; }
  const rlen = Math.hypot(rx, ry) || 1; rx /= rlen; ry /= rlen;
  const px = -ry, py = rx;
  const vx = pt[0] - prev[0], vy = pt[1] - prev[1];
  const along = vx * rx + vy * ry;
  const perp = vx * px + vy * py;
  if (Math.abs(along) >= Math.abs(perp)) return [Math.round(prev[0] + rx * along), Math.round(prev[1] + ry * along)];
  return [Math.round(prev[0] + px * perp), Math.round(prev[1] + py * perp)];
}

const PT_COLORS = { a: "#16a34a", b: "#2563eb" };

function CalPoints({ pts, fitv }) {
  return ["a", "b"].map((k) => pts[k] && (
    <div key={k} style={{ position: "absolute", left: pts[k][0] * fitv - 11, top: pts[k][1] * fitv - 11, background: PT_COLORS[k] }}
      className="w-[22px] h-[22px] rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-white shadow pointer-events-none">
      {k.toUpperCase()}
    </div>
  ));
}

function CalibrateDialog({ layer, onSave, onClose, refLayer }) {
  const baseDone = !!(refLayer && refLayer.points?.a && refLayer.points?.b && !refLayer.auto);
  const needBase = !!refLayer && !baseDone;
  const sheetInit = (layer.points?.a && layer.points?.b) ? { a: layer.points.a, b: layer.points.b } : {};
  const baseInit = baseDone ? { a: refLayer.points.a, b: refLayer.points.b } : {};
  const steps = [];
  for (const k of ["a", "b"]) { steps.push({ k, side: "sheet" }); if (needBase) steps.push({ k, side: "base" }); }
  let _init = 0;
  while (_init < steps.length) { const st = steps[_init]; if (!(st.side === "sheet" ? sheetInit[st.k] : baseInit[st.k])) break; _init++; }
  const [sheet, setSheet] = useState(sheetInit);
  const [basePts, setBasePts] = useState(baseInit);
  const [si, setSi] = useState(_init);
  const cur = steps[si] || null;

  const fit = Math.min(400 / layer.natural_w, 300 / layer.natural_h);
  const dispW = layer.natural_w * fit, dispH = layer.natural_h * fit;
  const rFit = refLayer ? Math.min(400 / refLayer.natural_w, 300 / refLayer.natural_h) : 1;
  const rW = refLayer ? refLayer.natural_w * rFit : 0, rH = refLayer ? refLayer.natural_h * rFit : 0;

  const click = (side, fitv) => (e) => {
    if (cur && cur.side !== side) { toast(side === "sheet" ? "This step wants a click on the SHEET (left)." : "This step wants a click on the BASE (right)."); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / fitv), y = Math.round((e.clientY - rect.top) / fitv);
    const k = cur ? cur.k : "a";
    (side === "sheet" ? setSheet : setBasePts)((p) => ({ ...p, [k]: [x, y] }));
    setSi((i) => Math.min(i + 1, steps.length));
  };
  const reset = () => { setSheet({}); setBasePts(baseDone ? { a: refLayer.points.a, b: refLayer.points.b } : {}); setSi(0); };
  const ready = sheet.a && sheet.b && (!needBase || (basePts.a && basePts.b));

  const hint = !cur ? "Both known points marked — click “Save & Snap” to line them up."
    : cur.side === "sheet" ? `Point ${cur.k.toUpperCase()} — click a known permanent feature (road junction, tank / river edge, railway point or plot corner) on ${refLayer ? "the UPLOADED SHEET" : "THIS MAP"}.`
      : `Point ${cur.k.toUpperCase()} — now click the SAME permanent feature on the SATELLITE BASE.`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[920px]">
        <DialogHeader>
          <DialogTitle>{refLayer ? `Match “${layer.name}” to the satellite base` : `Mark two reference features on “${layer.name}”`}</DialogTitle>
          <DialogDescription>{refLayer ? "Pick 2 known permanent features (A, B) that appear on BOTH maps — a road intersection, tank / river edge, railway point or plot corner, in any direction. For each: click it on the sheet, then click the same spot on the base — the sheet snaps onto the base and takes its ground scale automatically." : "Click 2 known permanent features (A, B) on this sheet — a road intersection, tank / river edge, railway point or plot corner, in any direction. It becomes the reference every other map lines up to."}</DialogDescription>
        </DialogHeader>
        <p className="text-sm font-semibold" style={{ color: cur ? PT_COLORS[cur.k] : "#16a34a" }} data-testid="gis-cal-hint">{hint}</p>
        <div className="flex flex-col md:flex-row gap-4 justify-center items-start">
          <div>
            <p className="text-[11px] font-semibold text-primary mb-1">1 · Uploaded sheet</p>
            <div className={`relative border-2 rounded-md overflow-hidden bg-muted/30 ${cur?.side === "sheet" ? "border-accent ring-2 ring-accent/40" : "border-border"}`} style={{ width: dispW, height: dispH }}>
              <img src={gisObjectUrl(layer.path)} alt={layer.name} draggable={false} onClick={click("sheet", fit)} style={{ width: dispW, height: dispH, cursor: "crosshair", display: "block" }} data-testid="gis-cal-image" />
              <CalPoints pts={sheet} fitv={fit} />
            </div>
          </div>
          {refLayer && (
            <div>
              <p className="text-[11px] font-semibold text-primary mb-1">2 · Satellite base{baseDone ? " · already marked" : ""}</p>
              <div className={`relative border-2 rounded-md overflow-hidden bg-muted/30 ${cur?.side === "base" ? "border-accent ring-2 ring-accent/40" : "border-border"}`} style={{ width: rW, height: rH }}>
                <img src={gisObjectUrl(refLayer.path)} alt="base" draggable={false} onClick={click("base", rFit)} style={{ width: rW, height: rH, cursor: needBase ? "crosshair" : "default", display: "block" }} data-testid="gis-cal-base-image" />
                <CalPoints pts={basePts} fitv={rFit} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={reset} data-testid="gis-cal-reset">Start over</Button>
          <Button type="button" className="rounded-full" disabled={!ready} onClick={() => onSave(sheet, needBase ? basePts : null)} data-testid="gis-cal-save">Save &amp; Snap</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const COMPASS_DEG = { N: 0, NNE: 22.5, NE: 45, ENE: 67.5, E: 90, ESE: 112.5, SE: 135, SSE: 157.5, S: 180, SSW: 202.5, SW: 225, WSW: 247.5, W: 270, WNW: 292.5, NW: 315, NNW: 337.5 };

function parseComparable(c) {
  const m = String(c.source || "").match(/([\d.]+)\s*km\s*([NSEW]{1,3})/i);
  if (!m) return null;
  const dist = parseFloat(m[1]);
  const deg = COMPASS_DEG[(m[2] || "").toUpperCase()];
  if (!isFinite(dist) || deg === undefined) return null;
  const rate = parseFloat(String(c.rate).replace(/[^\d.]/g, ""));
  return { dist, deg, rate: isFinite(rate) ? rate : 0, label: c.property || "", excluded: !!c.excluded };
}

function ComparablesRadar({ comparables }) {
  const pts = (comparables || []).map(parseComparable).filter(Boolean);
  if (pts.length === 0) return null;
  const C = 130, R = 102;
  const maxD = Math.max(...pts.map((p) => p.dist)) || 1;
  const rs = pts.filter((p) => !p.excluded && p.rate > 0).map((p) => p.rate).sort((a, b) => a - b);
  const med = rs.length ? (rs.length % 2 ? rs[(rs.length - 1) / 2] : (rs[rs.length / 2 - 1] + rs[rs.length / 2]) / 2) : 0;
  const dirPos = { N: [C, C - R - 4], E: [C + R + 6, C + 3], S: [C, C + R + 12], W: [C - R - 8, C + 3] };
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="gis-comparables-radar">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Comparables spread around plot</Label>
        <span className="text-[10px] text-muted-foreground">{pts.length} within {maxD.toFixed(1)} km</span>
      </div>
      <svg viewBox="0 0 260 260" className="w-full max-w-[240px] mx-auto">
        {[1 / 3, 2 / 3, 1].map((f, i) => (
          <g key={i}>
            <circle cx={C} cy={C} r={R * f} fill="none" stroke="#e2e8f0" strokeWidth={1} />
            <text x={C + 3} y={C - R * f + 3} fontSize={8} fill="#94a3b8">{(maxD * f).toFixed(1)}km</text>
          </g>
        ))}
        <line x1={C} y1={C - R} x2={C} y2={C + R} stroke="#e2e8f0" />
        <line x1={C - R} y1={C} x2={C + R} y2={C} stroke="#e2e8f0" />
        {Object.entries(dirPos).map(([d, p]) => <text key={d} x={p[0]} y={p[1]} fontSize={9} fontWeight="700" fill="#64748b" textAnchor="middle">{d}</text>)}
        {pts.map((p, i) => {
          const rad = (p.dist / maxD) * R;
          const x = C + rad * Math.sin((p.deg * Math.PI) / 180);
          const y = C - rad * Math.cos((p.deg * Math.PI) / 180);
          const fill = p.excluded ? "#cbd5e1" : (med && p.rate > med ? "#d97706" : med && p.rate < med ? "#16a34a" : "#64748b");
          return (
            <g key={i} data-testid={`gis-comp-dot-${i}`}>
              <circle cx={x} cy={y} r={5} fill={p.excluded ? "none" : fill} stroke={fill} strokeWidth={p.excluded ? 1.5 : 1} strokeDasharray={p.excluded ? "2 2" : undefined} />
              {p.rate > 0 && <text x={x} y={y - 7} fontSize={7.5} fontWeight="600" fill="#475569" textAnchor="middle">{`\u20b9${Math.round(p.rate / 100) / 10}k`}</text>}
            </g>
          );
        })}
        <path d={`M ${C} ${C} l -6 -13 a 6 6 0 1 1 12 0 z`} fill="#ea580c" stroke="#fff" strokeWidth={1.3} />
        <circle cx={C} cy={C - 13} r={2.5} fill="#fff" />
      </svg>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] text-muted-foreground justify-center">
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#ea580c]" /> Plot</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#d97706]" /> Above median</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#16a34a]" /> Below median</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full border border-slate-400" /> Excluded</span>
        {med > 0 && <span className="font-medium text-primary">Median \u20b9{Math.round(med).toLocaleString("en-IN")}/sq.ft</span>}
      </div>
    </div>
  );
}

export default function GisOverlayStudio({ value, onChange, coords, onApplyArea, bankName, branchName, comparables }) {
  const cfg = value || {};
  const layers = cfg.layers || [];
  const target = cfg.target || DEF_TARGET;
  const polygon = cfg.polygon || [];
  const measurements = cfg.measurements || [];
  const realDist = cfg.real_distance_m ?? "";
  const northDeg = cfg.north_deg ?? 0;
  const lockTarget = !!cfg.lock_target;
  const areaLabel = cfg.area_label || "";
  const incAerial = cfg.include_aerial !== false;
  const _abLen = Math.hypot((target.b?.[0] ?? 0) - (target.a?.[0] ?? 0), (target.b?.[1] ?? 0) - (target.a?.[1] ?? 0)) || 200;
  const [activeId, setActiveId] = useState(null);
  const activeLayer = layers.find((l) => l.id === activeId) || layers.find((l) => l.points?.a && l.points?.b) || layers[0] || null;
  const base = { stage: { w: STAGE_W, h: STAGE_H }, target, polygon, layers, measurements, real_distance_m: realDist, north_deg: northDeg, ...cfg };
  const patch = (p) => onChange({ ...base, ...p });

  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("khasra");
  const [calibrate, setCalibrate] = useState(null);
  const [mode, setMode] = useState("none");
  const [activeLine, setActiveLine] = useState(null);
  const [snap, setSnap] = useState(false);
  const [baseBusy, setBaseBusy] = useState(false);
  const [baseZoom, setBaseZoom] = useState(18);
  const [zoomReady, setZoomReady] = useState(false);
  const [baseSource, setBaseSource] = useState("satellite");
  const [autoAlign, setAutoAlign] = useState(true);
  const [manualCoords, setManualCoords] = useState(null);
  const [geoQuery, setGeoQuery] = useState("");
  const [geoBusy, setGeoBusy] = useState(false);
  const effCoords = coords || manualCoords;
  const findLocation = async () => {
    const q = geoQuery.trim();
    if (!q) return;
    const m = q.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (m) { setManualCoords({ lat: parseFloat(m[1]), lon: parseFloat(m[2]) }); toast.success("Location set — now add the Google satellite base"); return; }
    setGeoBusy(true);
    try {
      const { data } = await api.get("/gis/geocode", { params: { q } });
      setManualCoords({ lat: data.lat, lon: data.lon });
      toast.success(`Found: ${data.formatted_address}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Location not found"); }
    finally { setGeoBusy(false); }
  };
  const fileRef = useRef(null);
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.5);
  const [viewZoom, setViewZoom] = useState(1);
  const [winMul, setWinMul] = useState(1);

  useLayoutEffect(() => {
    const measure = () => { if (wrapRef.current) setScale(wrapRef.current.clientWidth / STAGE_W); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (layers.some((l) => !l.id)) {
      patch({ layers: layers.map((l, i) => (l.id ? l : { ...l, id: `gis_${Date.now().toString(36)}_${i}_${Math.random().toString(36).slice(2, 6)}` })) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  // Map a GeoTIFF's embedded coordinates onto the satellite base's A/B points so it overlays
  // without manual calibration. Mutates layer.points. Returns true if placed.
  const autoPlaceGeoTiff = (layer, tg) => {
    const sat = layers.find((l) => l.kind === "satellite" && l.geo && l.points?.a && l.points?.b);
    if (!sat) return false;
    const det = tg.dlon_dcol * tg.dlat_drow - tg.dlon_drow * tg.dlat_dcol;
    if (!det) return false;
    const g = sat.geo;
    const cosLat = Math.cos((g.lat * Math.PI) / 180) || 1;
    const baseToLL = (px, py) => {
      const dpx = px - sat.natural_w / 2, dpy = py - sat.natural_h / 2;
      return [g.lon + (dpx * g.mpp) / (111320 * cosLat), g.lat - (dpy * g.mpp) / 111320];
    };
    const llToTiffPx = (lon, lat) => {
      const X = lon - tg.lon0, Y = lat - tg.lat0;
      return [Math.round((tg.dlat_drow * X - tg.dlon_drow * Y) / det),
        Math.round((tg.dlon_dcol * Y - tg.dlat_dcol * X) / det)];
    };
    const [lonA, latA] = baseToLL(sat.points.a[0], sat.points.a[1]);
    const [lonB, latB] = baseToLL(sat.points.b[0], sat.points.b[1]);
    layer.points = { a: llToTiffPx(lonA, latA), b: llToTiffPx(lonB, latB) };
    return true;
  };

  const addFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/gis/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const layer = { id: `gis_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`, name: file.name.replace(/\.[^.]+$/, ""), kind, path: data.path, natural_w: data.width, natural_h: data.height, points: {}, opacity: 0.75, visible: true };
      // GeoTIFF auto-placement: if the file carries coordinates and a geo-registered satellite base exists,
      // compute the overlay's A/B pixels from the base's A/B world coordinates — no manual calibration needed.
      const geoPlaced = data.georeferenced && data.geo && autoPlaceGeoTiff(layer, data.geo);
      patch({ layers: [...layers, layer] });
      if (geoPlaced) {
        toast.success("GeoTIFF auto-aligned to the satellite base from its coordinates");
      } else {
        setCalibrate(layer);
        toast.success(data.georeferenced ? "GeoTIFF added — add a satellite base first to auto-align, or calibrate its two points" : "Layer added — calibrate its two points");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addSatelliteBase = async (replace = false) => {
    const c = effCoords;
    if (!c) return;
    setBaseBusy(true);
    try {
      const { data } = await api.post("/gis/base-from-coords", { lat: c.lat, lon: c.lon, zoom: baseZoom, source: baseSource });
      const w = data.width, h = data.height;
      let points = {};
      const extra = {};
      const auto = autoAlign && !!data.mpp;
      if (auto) {
        const ax = Math.round(w * 0.3), ay = Math.round(h * 0.5);
        const bx = Math.round(w * 0.7), by = Math.round(h * 0.5);
        points = { a: [ax, ay], b: [bx, by] };
        // Fill the frame: map the base so it covers the full stage edge-to-edge (object-fit:cover).
        const sCover = Math.max(STAGE_W / w, STAGE_H / h);
        const ox = (STAGE_W - w * sCover) / 2, oy = (STAGE_H - h * sCover) / 2;
        extra.target = { a: [Math.round(ox + ax * sCover), Math.round(oy + ay * sCover)], b: [Math.round(ox + bx * sCover), Math.round(oy + by * sCover)] };
        extra.real_distance_m = Math.round((bx - ax) * data.mpp);
      }
      const layer = { id: `gis_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`, name: baseSource === "street" ? "Street base" : baseSource === "hybrid" ? "Hybrid base" : "Satellite base", kind: "satellite", path: data.path, natural_w: w, natural_h: h, points, opacity: 1, visible: true, auto, geo: { lat: c.lat, lon: c.lon, mpp: data.mpp } };
      let kept = replace ? layers.filter((l) => l.kind !== "satellite") : layers;
      // Base was refreshed/replaced — existing calibrated sheets no longer match the new imagery.
      if (replace) kept = kept.map((l) => (l.points?.a && l.points?.b ? { ...l, needs_recalibration: true } : l));
      patch({ layers: [...kept, layer], ...extra });
      if ((bankName || "").trim()) api.put("/gis/branch-prefs", { bank: bankName, branch: branchName || "", gis_zoom: baseZoom, gis_source: baseSource }).catch(() => {});
      if (auto) {
        toast.success(replace ? `Base refreshed from Google at zoom ${baseZoom} — re-calibrate your sheets to the new base` : "Satellite base auto-aligned to map scale — calibrate other sheets to points A & B");
      } else {
        setCalibrate(layer);
        toast.success(replace ? "Base refreshed — now calibrate its points" : "Base map added — now calibrate its two points");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch base map");
    } finally {
      setBaseBusy(false);
    }
  };

  // Re-fetch the satellite at a wider zoom and position it to COVER the whole calibrated sheet,
  // so the imagery spreads across the entire khasra instead of sitting in one corner.
  const fitBaseToSheet = async () => {
    if (!effCoords) { toast("Set the plot location first."); return; }
    if (!target.a || !target.b) return;
    const overlays = layers.filter((l) => l.kind !== "satellite" && l.visible !== false && l.points?.a && l.points?.b);
    if (overlays.length === 0) { toast("Calibrate a khasra/map sheet first, then fit the base to it."); return; }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const l of overlays) {
      const m = similarity(l.points.a, l.points.b, target.a, target.b);
      if (!m) continue;
      for (const [ix, iy] of [[0, 0], [l.natural_w, 0], [0, l.natural_h], [l.natural_w, l.natural_h]]) {
        const sx = m.a * ix + m.c * iy + m.e, sy = m.b * ix + m.d * iy + m.f;
        minX = Math.min(minX, sx); minY = Math.min(minY, sy); maxX = Math.max(maxX, sx); maxY = Math.max(maxY, sy);
      }
    }
    const bboxW = maxX - minX, bboxH = maxY - minY;
    if (!(bboxW > 0 && bboxH > 0)) return;
    const sat = layers.find((l) => l.kind === "satellite" && l.points?.a && l.points?.b);
    let newZoom = baseZoom;
    if (sat) {
      const sm = similarity(sat.points.a, sat.points.b, target.a, target.b);
      const s = sm ? Math.hypot(sm.a, sm.b) : null;
      const coverK0 = Math.max(bboxW / sat.natural_w, bboxH / sat.natural_h);
      if (s && coverK0 > s) newZoom = Math.max(12, Math.min(20, Math.round(baseZoom - Math.log2(coverK0 / s))));
    }
    setBaseBusy(true);
    try {
      const { data } = await api.post("/gis/base-from-coords", { lat: effCoords.lat, lon: effCoords.lon, zoom: newZoom, source: baseSource });
      const w = data.width, h = data.height;
      const k = Math.max(bboxW / w, bboxH / h) * 1.04;
      const ox = minX - (w * k - bboxW) / 2, oy = minY - (h * k - bboxH) / 2;
      const inv = (p) => [Math.round((p[0] - ox) / k), Math.round((p[1] - oy) / k)];
      const newSat = { id: `gis_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`, name: baseSource === "street" ? "Street base" : baseSource === "hybrid" ? "Hybrid base" : "Satellite base", kind: "satellite", path: data.path, natural_w: w, natural_h: h, points: { a: inv(target.a), b: inv(target.b) }, opacity: 1, visible: true, auto: false, geo: { lat: effCoords.lat, lon: effCoords.lon, mpp: data.mpp } };
      const kept = layers.filter((l) => l.kind !== "satellite");
      setBaseZoom(newZoom);
      patch({ layers: [newSat, ...kept] });
      toast.success(`Base spread across the sheet (zoom ${newZoom}).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fit base to sheet");
    } finally {
      setBaseBusy(false);
    }
  };

  // Load the firm-wide default satellite zoom, then any per-branch (bank+branch) override, before auto-fetching the base.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/settings/firm");
        const z = parseInt(r.data?.gis_default_zoom, 10);
        if (active && Number.isFinite(z) && z >= 12 && z <= 20) setBaseZoom(z);
        const src = r.data?.gis_default_source;
        if (active && ["satellite", "street", "hybrid"].includes(src)) setBaseSource(src);
      } catch { /* firm defaults optional */ }
      if ((bankName || "").trim()) {
        try {
          const b = await api.get("/gis/branch-prefs", { params: { bank: bankName, branch: branchName || "" } });
          const bz = parseInt(b.data?.gis_zoom, 10);
          if (active && Number.isFinite(bz) && bz >= 12 && bz <= 20) setBaseZoom(bz);
          const bs = b.data?.gis_source;
          if (active && ["satellite", "street", "hybrid"].includes(bs)) setBaseSource(bs);
        } catch { /* branch pref optional */ }
      }
      if (active) setZoomReady(true);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankName, branchName]);

  // Auto-populate the Google satellite base as soon as a location is available (job
  // coordinates or a location picked via the in-studio search) and no base exists yet.
  const autoBaseRef = useRef("");
  useEffect(() => {
    if (!effCoords || !zoomReady || baseBusy) return;
    if (layers.some((l) => l.kind === "satellite")) return;
    const key = `${effCoords.lat},${effCoords.lon}`;
    if (autoBaseRef.current === key) return;
    autoBaseRef.current = key;
    addSatelliteBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effCoords?.lat, effCoords?.lon, zoomReady, layers.length]);
  const updateLayer = (id, upd) => patch({ layers: layers.map((l) => (l.id === id ? { ...l, ...upd } : l)) });
  const setAdj = (l, k, v) => updateLayer(l.id, { adjust: { rot: 0, zoom: 1, dx: 0, dy: 0, ...(l.adjust || {}), [k]: v } });
  const removeLayer = (id) => {
    const removed = layers.find((l) => l.id === id);
    const rest = layers.filter((l) => l.id !== id);
    const upd = { layers: rest };
    // Scale comes from the satellite base; if the last satellite base is removed, drop the stale ground
    // distance and flag the remaining calibrated sheets as needing re-calibration against a fresh base.
    if (removed?.kind === "satellite" && !rest.some((l) => l.kind === "satellite")) {
      upd.real_distance_m = "";
      upd.layers = rest.map((l) => (l.points?.a && l.points?.b ? { ...l, needs_recalibration: true } : l));
    }
    patch(upd);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= layers.length) return;
    const next = layers.slice();
    [next[i], next[j]] = [next[j], next[i]];
    patch({ layers: next });
  };

  const dragRef = useRef(null);
  const movedRef = useRef(false);

  const onStageClick = (e) => {
    if (movedRef.current) { movedRef.current = false; return; }
    if (mode === "none") return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / (scale * winMul * viewZoom));
    const y = Math.round((e.clientY - rect.top) / (scale * winMul * viewZoom));
    if (mode === "area") {
      if (polygon.length >= 3) {
        const f = polygon[0];
        if (Math.hypot(x - f[0], y - f[1]) <= 16) { setMode("none"); toast.success("Boundary closed"); return; }
      }
      let pt = [x, y];
      if (snap && polygon.length >= 1) pt = snapRightAngle(polygon.length >= 2 ? polygon[polygon.length - 2] : null, polygon[polygon.length - 1], pt);
      patch({ polygon: [...polygon, pt] });
      return;
    }
    // measure mode
    let idx = activeLine;
    const next = measurements.slice();
    if (idx == null || idx >= next.length) {
      next.push({ id: `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`, points: [[x, y]] });
      idx = next.length - 1;
      setActiveLine(idx);
    } else {
      const pts = next[idx].points || [];
      let pt = [x, y];
      if (snap && pts.length >= 1) pt = snapRightAngle(pts.length >= 2 ? pts[pts.length - 2] : null, pts[pts.length - 1], pt);
      next[idx] = { ...next[idx], points: [...pts, pt] };
    }
    patch({ measurements: next });
  };

  const updateMeasurement = (idx, upd) => patch({ measurements: measurements.map((m, i) => (i === idx ? { ...m, ...upd } : m)) });
  const removeMeasurement = (idx) => { patch({ measurements: measurements.filter((_, i) => i !== idx) }); setActiveLine((a) => (a === idx ? null : a)); };

  const onVertexDown = (kind, pIdx, mIdx) => (e) => {
    if (mode !== "none") return;
    e.stopPropagation();
    dragRef.current = { kind, pIdx, mIdx };
    movedRef.current = false;
  };
  const onTargetDown = (which) => (e) => {
    if (lockTarget) return;
    e.stopPropagation();
    dragRef.current = { kind: "target", which };
    movedRef.current = false;
  };
  const onStagePointerMove = (e) => {
    if (!dragRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / (scale * winMul * viewZoom));
    const y = Math.round((e.clientY - rect.top) / (scale * winMul * viewZoom));
    movedRef.current = true;
    const d = dragRef.current;
    if (d.kind === "poly") {
      const next = polygon.slice(); next[d.pIdx] = [x, y]; patch({ polygon: next });
    } else if (d.kind === "target") {
      patch({ target: { ...target, [d.which]: [x, y] } });
    } else {
      const next = measurements.slice();
      const pts = (next[d.mIdx].points || []).slice(); pts[d.pIdx] = [x, y];
      next[d.mIdx] = { ...next[d.mIdx], points: pts }; patch({ measurements: next });
    }
  };
  const onStagePointerUp = () => { dragRef.current = null; };

  // Rotate the whole aligned overlay (all layers) about the stage centre by rotating the
  // common target line. Lets the user straighten the sheet to North without per-layer edits.
  const rotateOverlay = (deltaDeg) => {
    if (!target.a || !target.b) return;
    const cx = STAGE_W / 2, cy = STAGE_H / 2;
    const th = (deltaDeg * Math.PI) / 180, cos = Math.cos(th), sin = Math.sin(th);
    const rot = (p) => [Math.round(cx + (p[0] - cx) * cos - (p[1] - cy) * sin), Math.round(cy + (p[0] - cx) * sin + (p[1] - cy) * cos)];
    const nt = { a: rot(target.a), b: rot(target.b) };
    patch({ target: nt });
  };

  const area = useMemo(() => {
    const rd = parseFloat(realDist);
    if (!target.a || !target.b || !rd || polygon.length < 3) return null;
    const screen = Math.hypot(target.b[0] - target.a[0], target.b[1] - target.a[1]);
    if (!screen) return null;
    const mpp = rd / screen;
    let s = 0;
    for (let i = 0; i < polygon.length; i++) {
      const [x1, y1] = polygon[i];
      const [x2, y2] = polygon[(i + 1) % polygon.length];
      s += x1 * y2 - x2 * y1;
    }
    const sqm = (Math.abs(s) / 2) * mpp * mpp;
    return { sqm, sqft: sqm * 10.7639, acres: sqm / 4046.856 };
  }, [target, realDist, polygon]);

  const lengths = useMemo(() => {
    const rd = parseFloat(realDist);
    if (!target.a || !target.b || !rd) return [];
    const ab = Math.hypot(target.b[0] - target.a[0], target.b[1] - target.a[1]);
    if (!ab) return [];
    const mpp = rd / ab;
    return measurements.map((ml, i) => {
      const pts = ml.points || [];
      let L = 0;
      for (let j = 0; j < pts.length - 1; j++) L += Math.hypot(pts[j + 1][0] - pts[j][0], pts[j + 1][1] - pts[j][1]);
      return { id: ml.id || i, label: ml.label || `Line ${i + 1}`, m: L * mpp, ft: L * mpp * 3.28084, n: pts.length };
    }).filter((x) => x.n >= 2);
  }, [measurements, target, realDist]);

  const downloadPng = async () => {
    const t = toast.loading("Rendering overlay…");
    try {
      const res = await api.post("/gis/composite", { config: base }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
      const a = document.createElement("a");
      a.href = url; a.download = "gis-overlay.png"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.dismiss(t);
    } catch (e) { toast.dismiss(t); toast.error("Could not render overlay"); }
  };

  const calibrated = layers.filter((l) => l.points?.a && l.points?.b && l.visible !== false);
  const baseRef = layers.find((l) => l.auto && l.points?.a && l.points?.b) || layers.find((l) => l.kind === "satellite" && l.points?.a && l.points?.b) || null;

  return (
    <div className="space-y-4" data-testid="gis-studio">
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        {/* Stage */}
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Button type="button" size="sm" variant={mode === "area" ? "default" : "outline"} className="rounded-full text-xs" onClick={() => { setMode((m) => (m === "area" ? "none" : "area")); setActiveLine(null); }} data-testid="gis-draw-toggle">
              <Polygon size={14} className="mr-1" /> {mode === "area" ? "Tracing area…" : "Trace Area"}
            </Button>
            <Button type="button" size="sm" variant={mode === "measure" ? "default" : "outline"} className="rounded-full text-xs" onClick={() => { setMode((m) => (m === "measure" ? "none" : "measure")); setActiveLine(null); }} data-testid="gis-measure-toggle">
              <Ruler size={14} className="mr-1" /> {mode === "measure" ? "Measuring…" : "Measure Lengths"}
            </Button>
            {mode === "measure" && <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => setActiveLine(null)} data-testid="gis-new-line"><Plus size={13} className="mr-1" /> New line</Button>}
            {mode === "area" && (
              <>
                <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={!polygon.length} onClick={() => patch({ polygon: polygon.slice(0, -1) })} data-testid="gis-undo-point">Undo point</Button>
                <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={!polygon.length} onClick={() => patch({ polygon: [] })} data-testid="gis-clear-poly">Clear area</Button>
                <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={polygon.length < 3} onClick={() => { setMode("none"); toast.success("Boundary closed"); }} data-testid="gis-close-boundary">Close boundary</Button>
              </>
            )}
            {mode === "measure" && <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={!measurements.length} onClick={() => { patch({ measurements: [] }); setActiveLine(null); }} data-testid="gis-clear-measures">Clear lines</Button>}
            {mode !== "none" && <Button type="button" size="sm" variant={snap ? "default" : "outline"} className="rounded-full text-xs" onClick={() => setSnap((v) => !v)} data-testid="gis-snap-toggle"><Square size={14} className="mr-1" /> {snap ? "Right-angle: ON" : "Right-angle snap"}</Button>}
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={!calibrated.length} onClick={downloadPng} data-testid="gis-download-png"><DownloadSimple size={14} className="mr-1" /> PNG</Button>
            <Button type="button" size="sm" variant={incAerial ? "default" : "outline"} className="rounded-full text-xs" title="Show the dedicated Aerial View with Plot Boundary page in the PDF" onClick={() => patch({ include_aerial: !incAerial })} data-testid="gis-aerial-toggle">
              {incAerial ? "Aerial page: ON" : "Aerial page: OFF"}
            </Button>
            <Button type="button" size="sm" variant={lockTarget ? "default" : "outline"} className="rounded-full text-xs" onClick={() => patch({ lock_target: !lockTarget })} data-testid="gis-lock-target">
              {lockTarget ? <Lock size={14} className="mr-1" /> : <LockOpen size={14} className="mr-1" />} {lockTarget ? "A/B/C Locked" : "Lock A/B/C"}
            </Button>
            {lockTarget && (
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { patch({ lock_target: false }); toast.success("A/B/C unlocked — drag the handles to reposition"); }} data-testid="gis-reposition-target">
                <Crosshair size={14} className="mr-1" /> Reposition A/B/C
              </Button>
            )}
          </div>
          <div ref={wrapRef} className="relative w-full rounded-lg border bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:24px_24px]" style={{ height: STAGE_H * scale * winMul, overflow: viewZoom > 1 || winMul > 1 ? "auto" : "hidden" }} data-testid="gis-stage">
            <div className="absolute top-0 left-0" style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale * winMul * viewZoom})`, transformOrigin: "top left", cursor: mode !== "none" ? "crosshair" : "default" }} onClick={onStageClick} onPointerMove={onStagePointerMove} onPointerUp={onStagePointerUp} onPointerLeave={onStagePointerUp}>
              {layers.map((l, i) => {
                if (l.visible === false || !(l.points?.a && l.points?.b)) return null;
                const m = similarity(l.points.a, l.points.b, target.a, target.b);
                if (!m) return null;
                const adj = l.adjust || {};
                const zoom = adj.zoom ?? 1, rot = adj.rot ?? 0, mdx = adj.dx ?? 0, mdy = adj.dy ?? 0;
                const cx = (l.natural_w || 0) / 2, cy = (l.natural_h || 0) / 2;
                const manual = `translate(${mdx}px,${mdy}px) translate(${cx}px,${cy}px) rotate(${rot}deg) scale(${zoom}) translate(${-cx}px,${-cy}px)`;
                return (
                  <img key={l.id || i} src={gisObjectUrl(l.path)} alt={l.name} draggable={false}
                    style={{ position: "absolute", top: 0, left: 0, width: l.natural_w, height: l.natural_h, transformOrigin: "0 0", transform: `matrix(${m.a},${m.b},${m.c},${m.d},${m.e},${m.f}) ${manual}`, opacity: l.opacity ?? 0.75, pointerEvents: "none", userSelect: "none" }} />
                );
              })}
              <svg width={STAGE_W} height={STAGE_H} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
                <line x1={target.a[0]} y1={target.a[1]} x2={target.b[0]} y2={target.b[1]} stroke="#0A2540" strokeWidth={2} strokeDasharray="6 4" opacity={0.7} />
                {polygon.length >= 2 && <polyline points={[...polygon, ...(polygon.length >= 3 ? [polygon[0]] : [])].map((p) => p.join(",")).join(" ")} fill={polygon.length >= 3 ? "rgba(220,38,38,0.15)" : "none"} stroke="#dc2626" strokeWidth={2.5} />}
                {polygon.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={i === 0 && mode === "area" && polygon.length >= 3 ? 8 : 5} fill="#dc2626" stroke={i === 0 && mode === "area" && polygon.length >= 3 ? "#fff" : "none"} strokeWidth={2} onPointerDown={onVertexDown("poly", i)} style={{ pointerEvents: mode === "none" ? "all" : "none", cursor: "grab" }} />)}
                {polygon.length >= 3 && (() => {
                  const cx = polygon.reduce((s, p) => s + p[0], 0) / polygon.length;
                  const cy = polygon.reduce((s, p) => s + p[1], 0) / polygon.length;
                  const txt = `${areaLabel ? areaLabel + " · " : ""}${area ? area.sqft.toLocaleString("en-IN", { maximumFractionDigits: 0 }) + " sq.ft" : "set A→B scale"}`;
                  return (
                    <text x={cx} y={cy} textAnchor="middle" fontSize={13} fontWeight="700" fill="#b91c1c" paintOrder="stroke" stroke="#fff" strokeWidth={3} strokeLinejoin="round" onClick={(e) => { e.stopPropagation(); const v = window.prompt("Label for this area (e.g. Plot, Building footprint)", areaLabel); if (v !== null) patch({ area_label: v.trim() }); }} style={{ pointerEvents: mode === "none" ? "all" : "none", cursor: "text" }} data-testid="gis-area-label">{txt}</text>
                  );
                })()}
                {measurements.map((ml, i) => {
                  const pts = ml.points || [];
                  if (pts.length < 1) return null;
                  const len = lengths.find((l) => l.id === (ml.id || i));
                  const mid = pts[Math.floor(pts.length / 2)];
                  return (
                    <g key={ml.id || i}>
                      {pts.length >= 2 && <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#2563eb" strokeWidth={2.5} strokeDasharray={i === activeLine ? "7 4" : undefined} />}
                      {pts.map((p, j) => <circle key={j} cx={p[0]} cy={p[1]} r={4} fill="#2563eb" onPointerDown={onVertexDown("measure", j, i)} style={{ pointerEvents: mode === "none" ? "all" : "none", cursor: "grab" }} />)}
                      {len && pts.length >= 2 && <text x={mid[0] + 7} y={mid[1] - 7} fontSize={13} fontWeight="700" fill="#2563eb" onClick={(e) => { e.stopPropagation(); const v = window.prompt("Label for this line (e.g. East boundary, Road frontage)", ml.label || ""); if (v !== null) updateMeasurement(i, { label: v.trim() }); }} style={{ pointerEvents: mode === "none" ? "all" : "none", cursor: "text" }} data-testid={`gis-line-label-${i}`}>{ml.label ? `${ml.label}: ` : ""}{len.m.toFixed(1)} m</text>}
                    </g>
                  );
                })}
                {[["A", "a", target.a], ["B", "b", target.b]].map(([lb, key, p]) => {
                  return (
                    <g key={lb}>
                      <circle cx={p[0]} cy={p[1]} r={16} fill="transparent" onPointerDown={onTargetDown(key)} style={{ pointerEvents: lockTarget ? "none" : "all", cursor: lockTarget ? "default" : "grab" }} data-testid={`gis-target-${key}`} />
                      <circle cx={p[0]} cy={p[1]} r={9} fill={lockTarget ? "#15803d" : "#0A2540"} stroke="#fff" strokeWidth={3} style={{ pointerEvents: "none" }} />
                      <text x={p[0] + 13} y={p[1] + 5} fontSize={16} fontWeight="700" fill="#0A2540" style={{ pointerEvents: "none" }}>{lb}</text>
                    </g>
                  );
                })}
                {coords && (() => {
                  let px = STAGE_W / 2, py = STAGE_H / 2;
                  if (polygon.length >= 3) { px = polygon.reduce((s, p) => s + p[0], 0) / polygon.length; py = polygon.reduce((s, p) => s + p[1], 0) / polygon.length; }
                  return (
                    <g pointerEvents="none" data-testid="gis-plot-pin">
                      <path d={`M ${px} ${py} l -7 -15 a 7 7 0 1 1 14 0 z`} fill="#ea580c" stroke="#fff" strokeWidth={1.5} />
                      <circle cx={px} cy={py - 15} r={3} fill="#fff" />
                      <text x={px + 10} y={py - 11} fontSize={12} fontWeight="700" fill="#ea580c">{coords.lat.toFixed(6)}, {coords.lon.toFixed(6)}</text>
                    </g>
                  );
                })()}
                {(() => {
                  const nx = STAGE_W - 50, ny = 58; const ang = (northDeg * Math.PI) / 180;
                  const ux = Math.sin(ang), uy = -Math.cos(ang); const L = 26;
                  const tip = [nx + ux * L, ny + uy * L]; const tail = [nx - ux * L * 0.6, ny - uy * L * 0.6];
                  const perp = [-uy, ux]; const hw = 6;
                  return (
                    <g>
                      <line x1={tail[0]} y1={tail[1]} x2={tip[0]} y2={tip[1]} stroke="#0A2540" strokeWidth={3} />
                      <polygon points={`${tip[0]},${tip[1]} ${tip[0] - ux * 11 + perp[0] * hw},${tip[1] - uy * 11 + perp[1] * hw} ${tip[0] - ux * 11 - perp[0] * hw},${tip[1] - uy * 11 - perp[1] * hw}`} fill="#0A2540" />
                      <text x={nx - 5} y={ny - L - 5} fontSize={15} fontWeight="700" fill="#0A2540">N</text>
                    </g>
                  );
                })()}
                {(() => {
                  const rd = parseFloat(realDist); if (!target.a || !target.b || !rd) return null;
                  const ab = Math.hypot(target.b[0] - target.a[0], target.b[1] - target.a[1]); if (!ab) return null;
                  const mpp = rd / ab; const meters = niceNum(120 * mpp); const barPx = meters / mpp;
                  const bx = 30, by = STAGE_H - 28;
                  return (
                    <g>
                      <rect x={bx - 6} y={by - 24} width={barPx + 14} height={34} fill="rgba(255,255,255,0.72)" rx={4} />
                      <line x1={bx} y1={by} x2={bx + barPx} y2={by} stroke="#0A2540" strokeWidth={3} />
                      <line x1={bx} y1={by - 6} x2={bx} y2={by + 6} stroke="#0A2540" strokeWidth={3} />
                      <line x1={bx + barPx} y1={by - 6} x2={bx + barPx} y2={by + 6} stroke="#0A2540" strokeWidth={3} />
                      <text x={bx} y={by - 9} fontSize={13} fontWeight="700" fill="#0A2540">{fmtM(meters)}</text>
                    </g>
                  );
                })()}
              </svg>
            </div>
            {calibrated.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground px-6 text-center pointer-events-none">
                {baseBusy
                  ? "Loading satellite imagery for the plot location…"
                  : coords
                    ? "Preparing the satellite base from the plot's latitude & longitude…"
                    : "Add a map layer to begin. Pick two known permanent features — a road junction, tank or river edge, railway point or plot corner — that appear on every sheet. These two points (A \u0026 B), in any direction, align and scale all layers to the satellite base."}
              </div>
            )}
          </div>

          {calibrated.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="gis-rotate-overlay">
              <span className="text-[11px] text-muted-foreground mr-1">Straighten overlay to North:</span>
              <Button type="button" size="sm" variant="outline" disabled={lockTarget} className="rounded-full h-7 text-xs px-2" onClick={() => rotateOverlay(-5)} data-testid="gis-rotate-overlay-ccw5"><ArrowCounterClockwise size={13} className="mr-1" /> 5°</Button>
              <Button type="button" size="sm" variant="outline" disabled={lockTarget} className="rounded-full h-7 text-xs px-2" onClick={() => rotateOverlay(-1)} data-testid="gis-rotate-overlay-ccw1">−1°</Button>
              <Button type="button" size="sm" variant="outline" disabled={lockTarget} className="rounded-full h-7 text-xs px-2" onClick={() => rotateOverlay(1)} data-testid="gis-rotate-overlay-cw1">+1°</Button>
              <Button type="button" size="sm" variant="outline" disabled={lockTarget} className="rounded-full h-7 text-xs px-2" onClick={() => rotateOverlay(5)} data-testid="gis-rotate-overlay-cw5"><ArrowClockwise size={13} className="mr-1" /> 5°</Button>
              <span className="text-[10px] text-muted-foreground">{lockTarget ? "unlock A/B/C to rotate" : "rotates all layers together (keeps the North arrow fixed)"}</span>
            </div>
          )}

          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="gis-window-size">
            <span className="text-[11px] text-muted-foreground mr-1">Window size:</span>
            <Button type="button" size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" onClick={() => setWinMul((m) => Math.max(1, +(m - 0.25).toFixed(2)))} data-testid="gis-window-smaller">−</Button>
            <input type="range" min={1} max={3} step={0.25} value={winMul} onChange={(e) => setWinMul(parseFloat(e.target.value))} className="w-40 accent-primary" data-testid="gis-window-slider" />
            <Button type="button" size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" onClick={() => setWinMul((m) => Math.min(3, +(m + 0.25).toFixed(2)))} data-testid="gis-window-bigger">+</Button>
            <span className="text-[11px] w-10 text-right">{Math.round(winMul * 100)}%</span>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setWinMul(1)} data-testid="gis-window-reset">Reset</Button>
            <span className="text-[10px] text-muted-foreground">enlarges the whole working window (scroll to reach edges)</span>
          </div>

          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="gis-view-zoom">
            <span className="text-[11px] text-muted-foreground mr-1">Zoom view:</span>
            <Button type="button" size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" onClick={() => setViewZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} data-testid="gis-view-zoom-out">−</Button>
            <input type="range" min={1} max={4} step={0.25} value={viewZoom} onChange={(e) => setViewZoom(parseFloat(e.target.value))} className="w-40 accent-accent" data-testid="gis-view-zoom-slider" />
            <Button type="button" size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" onClick={() => setViewZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} data-testid="gis-view-zoom-in">+</Button>
            <span className="text-[11px] w-10 text-right">{Math.round(viewZoom * 100)}%</span>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setViewZoom(1)} data-testid="gis-view-zoom-reset">Reset</Button>
            <span className="text-[10px] text-muted-foreground">magnifies the canvas for precise alignment (scroll to pan)</span>
          </div>

          {layers.length > 0 && activeLayer && (
            <div className="mt-2 rounded-lg border-2 border-accent/30 bg-accent/5 p-2.5 space-y-1.5" data-testid="gis-active-controls">
              <div className="flex items-center gap-2">
                <img src={gisObjectUrl(activeLayer.path)} alt="" className="w-7 h-7 rounded object-cover border shrink-0" />
                <span className="text-xs font-semibold text-primary truncate flex-1">Adjusting: {activeLayer.name}</span>
                {layers.length > 1 && (
                  <select value={activeLayer.id} onChange={(e) => setActiveId(e.target.value)} className="h-7 text-[11px] border rounded px-1 bg-background" data-testid="gis-active-layer-select">
                    {layers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground w-12">Opacity</span>
                <input type="range" min={0.1} max={1} step={0.05} value={activeLayer.opacity ?? 0.75} onChange={(e) => updateLayer(activeLayer.id, { opacity: parseFloat(e.target.value) })} className="flex-1 accent-[#0A2540]" data-testid="gis-active-opacity" />
                <span className="text-[10px] w-8 text-right">{Math.round((activeLayer.opacity ?? 0.75) * 100)}%</span>
              </div>
              {activeLayer.points?.a && activeLayer.points?.b ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-12">Rotate</span>
                    <input type="range" min={-180} max={180} step={0.5} value={activeLayer.adjust?.rot ?? 0} onChange={(e) => setAdj(activeLayer, "rot", parseFloat(e.target.value))} className="flex-1 accent-accent" data-testid="gis-active-rotate" />
                    <input type="number" step={0.5} value={activeLayer.adjust?.rot ?? 0} onChange={(e) => setAdj(activeLayer, "rot", parseFloat(e.target.value) || 0)} className="w-12 h-6 text-[10px] border rounded px-1 text-right" data-testid="gis-active-rotate-num" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-12">Zoom</span>
                    <input type="range" min={0.3} max={3} step={0.02} value={activeLayer.adjust?.zoom ?? 1} onChange={(e) => setAdj(activeLayer, "zoom", parseFloat(e.target.value))} className="flex-1 accent-accent" data-testid="gis-active-zoom" />
                    <span className="text-[10px] w-8 text-right">{Math.round((activeLayer.adjust?.zoom ?? 1) * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground w-12">Nudge</span>
                    <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(activeLayer, "dx", (activeLayer.adjust?.dx ?? 0) - 10)} data-testid="gis-active-nudge-left"><ArrowLeft size={12} /></Button>
                    <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(activeLayer, "dx", (activeLayer.adjust?.dx ?? 0) + 10)} data-testid="gis-active-nudge-right"><ArrowRight size={12} /></Button>
                    <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(activeLayer, "dy", (activeLayer.adjust?.dy ?? 0) - 10)} data-testid="gis-active-nudge-up"><ArrowUp size={12} /></Button>
                    <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(activeLayer, "dy", (activeLayer.adjust?.dy ?? 0) + 10)} data-testid="gis-active-nudge-down"><ArrowDown size={12} /></Button>
                    <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] ml-auto" onClick={() => updateLayer(activeLayer.id, { adjust: { rot: 0, zoom: 1, dx: 0, dy: 0 } })} data-testid="gis-active-adjust-reset">Reset</Button>
                  </div>
                </>
              ) : <p className="text-[10px] text-amber-600">Calibrate this layer to enable rotate / zoom / nudge.</p>}
            </div>
          )}

          {/* Scale + area */}
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Ruler size={13} /> Ground distance A → B (m) <span className="text-red-600">*</span></Label>
              <Input type="number" value={realDist} onChange={(e) => patch({ real_distance_m: e.target.value })} placeholder="e.g. 50" className={`h-9 ${!parseFloat(realDist) ? "border-red-400 ring-1 ring-red-300" : ""}`} data-testid="gis-real-distance" />
              <p className="text-[10px] text-muted-foreground">{!parseFloat(realDist) ? "Real-world distance between your two known points (A → B). Auto-fills from the satellite base, or type it to unlock length & area." : "Scale is set from points A & B — lengths & area will calculate. Override above only if you have an exact survey distance."}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">North rotation (° clockwise)</Label>
              <Input type="number" value={northDeg} onChange={(e) => patch({ north_deg: parseFloat(e.target.value) || 0 })} placeholder="0 = up" className="h-9" data-testid="gis-north-deg" />
            </div>
            <div className="rounded-md border bg-muted/30 p-2.5" data-testid="gis-area-readout">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">Captioned Area</div>
              {polygon.length >= 3 && (
                <Input value={areaLabel} onChange={(e) => patch({ area_label: e.target.value })} placeholder="Area label (e.g. Plot)" className="h-7 text-xs mt-1 mb-1" data-testid="gis-area-label-input" />
              )}
              {area ? (
                <div className="text-sm font-semibold text-primary">
                  {area.sqft.toLocaleString("en-IN", { maximumFractionDigits: 0 })} sq.ft
                  <span className="text-muted-foreground font-normal block text-xs">{area.sqm.toLocaleString("en-IN", { maximumFractionDigits: 1 })} sq.m · {area.acres.toFixed(4)} acres</span>
                  {onApplyArea && (
                    <button type="button" onClick={() => onApplyArea(Math.round(area.sqft))}
                      className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent hover:bg-accent/25 px-2 py-0.5 text-[11px] font-medium transition-colors"
                      data-testid="gis-apply-area-btn">Use in Valuation (Land Area)</button>
                  )}
                </div>
              ) : <div className="text-xs text-muted-foreground">Enter A→B distance and trace a 3+ point area.</div>}
            </div>
          </div>
          {lengths.length > 0 && (
            <div className="mt-2 rounded-md border bg-muted/20 p-2.5" data-testid="gis-lengths-readout">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Measured Lengths (road frontage / sides)</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {lengths.map((l) => (
                  <span key={l.id} className="text-xs text-primary font-medium">{l.label}: {l.m.toFixed(1)} m <span className="text-muted-foreground">({l.ft.toFixed(0)} ft)</span></span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Layer panel */}
        <div className="space-y-3">
          <div className="rounded-lg border p-3 space-y-2">
            <Label className="text-xs">Map type for next upload</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9 text-sm" data-testid="gis-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
            <input ref={fileRef} type="file" accept="image/*,.tif,.tiff,image/tiff" className="hidden" onChange={(e) => addFile(e.target.files?.[0])} data-testid="gis-file-input" />
            <Button type="button" size="sm" className="rounded-full text-xs w-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="gis-add-layer">
              <Plus size={14} className="mr-1" /> {uploading ? "Uploading…" : "Add Map Layer"}
            </Button>
            {(
              <div className="pt-2 mt-1 border-t space-y-2">
                <Label className="text-[11px] text-muted-foreground">Live Google base map</Label>
                {!effCoords && (
                  <div className="space-y-1.5" data-testid="gis-geo-panel">
                    <p className="text-[11px] text-muted-foreground">No plot location yet. Search an address/place or paste “lat, long” to load the live Google satellite base.</p>
                    <div className="flex gap-1.5">
                      <Input value={geoQuery} onChange={(e) => setGeoQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); findLocation(); } }} placeholder="Address or 21.7581, 81.9877" className="h-8 text-xs" data-testid="gis-geo-search" />
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" disabled={geoBusy} onClick={findLocation} data-testid="gis-geo-find">{geoBusy ? "…" : "Find"}</Button>
                    </div>
                  </div>
                )}
                {effCoords && (
                  <>
                    {manualCoords && !coords && (
                      <p className="text-[10px] text-emerald-600">
                        Location set: {manualCoords.lat.toFixed(5)}, {manualCoords.lon.toFixed(5)}
                        <button type="button" className="underline ml-1.5" onClick={() => { setManualCoords(null); setGeoQuery(""); }} data-testid="gis-geo-clear">change</button>
                      </p>
                    )}
                    <div className="flex gap-1.5">
                      <Select value={baseSource} onValueChange={setBaseSource}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid="gis-base-source"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="satellite">Satellite</SelectItem><SelectItem value="hybrid">Satellite + labels</SelectItem><SelectItem value="street">Street (OSM)</SelectItem></SelectContent>
                      </Select>
                      <Select value={String(baseZoom)} onValueChange={(v) => setBaseZoom(parseInt(v, 10))}>
                        <SelectTrigger className="h-8 text-xs w-[92px]" data-testid="gis-base-zoom"><SelectValue /></SelectTrigger>
                        <SelectContent>{[16, 17, 18, 19, 20].map((z) => <SelectItem key={z} value={String(z)}>Zoom {z}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button type="button" size="sm" variant="outline" className="rounded-full text-xs w-full" disabled={baseBusy} onClick={() => addSatelliteBase(false)} data-testid="gis-add-base">
                      <Plus size={13} className="mr-1" /> {baseBusy ? "Fetching…" : "Add Satellite Base"}
                    </Button>
                    {layers.some((l) => l.kind === "satellite") && (
                      <Button type="button" size="sm" variant="ghost" className="rounded-full text-xs w-full text-primary" disabled={baseBusy} onClick={() => addSatelliteBase(true)} data-testid="gis-refresh-base">
                        <ArrowClockwise size={13} className="mr-1" /> {baseBusy ? "Refreshing…" : `Refresh base from Google (zoom ${baseZoom})`}
                      </Button>
                    )}
                    {layers.some((l) => l.kind === "satellite") && layers.some((l) => l.kind !== "satellite" && l.points?.a && l.points?.b) && (
                      <Button type="button" size="sm" variant="outline" className="rounded-full text-xs w-full border-accent text-accent" disabled={baseBusy} onClick={fitBaseToSheet} data-testid="gis-fit-base">
                        <ArrowsOut size={13} className="mr-1" /> {baseBusy ? "Fitting…" : "Fit base to sheet (spread satellite)"}
                      </Button>
                    )}
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                      <input type="checkbox" checked={autoAlign} onChange={(e) => setAutoAlign(e.target.checked)} className="accent-[#0A2540]" data-testid="gis-base-autoalign" />
                      Auto-align to map scale (skip calibrating this base)
                    </label>
                  </>
                )}
              </div>
            )}
          </div>

          {layers.some((l) => l.kind === "satellite") && !layers.some((l) => l.kind === "khasra" || l.kind === "masterplan") && (
            <div className="rounded-lg border border-accent/40 bg-accent/5 p-3 space-y-2" data-testid="gis-khasra-nudge">
              <p className="text-xs text-primary font-semibold">Next step: add the Khasra / Master-Plan sheet</p>
              <p className="text-[11px] text-muted-foreground">The satellite base is set. Upload the Khasra / cadastral or master-plan sheet and mark the same two known features (A &amp; B) on it — a road intersection, tank / river edge, railway point or plot corner. The overlay snaps to the base and takes its ground scale automatically.</p>
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs w-full" disabled={uploading} onClick={() => { setKind("khasra"); fileRef.current?.click(); }} data-testid="gis-khasra-upload">
                <Plus size={13} className="mr-1" /> Upload Khasra / Master-Plan sheet
              </Button>
            </div>
          )}

          <div className="space-y-2" data-testid="gis-layer-list">
            {layers.length === 0 && <p className="text-xs text-muted-foreground">No layers yet.</p>}
            {layers.map((l, i) => {
              const ok = l.points?.a && l.points?.b;
              return (
                <div key={l.id || i} className="rounded-lg border p-2.5 space-y-2 bg-background" data-testid={`gis-layer-${l.id}`}>
                  <div className="flex items-center gap-2">
                    <img src={gisObjectUrl(l.path)} alt="" className="w-9 h-9 rounded object-cover border bg-muted shrink-0" data-testid={`gis-thumb-${l.id}`} />
                    <span className="text-xs font-semibold text-primary truncate flex-1 cursor-pointer hover:underline" title="Click to adjust this layer beside the map" onClick={() => setActiveId(l.id)}>{l.name}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">{(KINDS.find((k) => k.v === l.kind) || {}).label || l.kind}</span>
                  </div>
                  {!ok && <span className="text-[10px] text-amber-600 font-medium">Needs calibration</span>}
                  {ok && l.needs_recalibration && (
                    <div className="flex items-start gap-1 rounded bg-amber-50 border border-amber-200 px-1.5 py-1" data-testid={`gis-recalib-${l.id}`}>
                      <Warning size={12} className="text-amber-600 mt-0.5 shrink-0" />
                      <span className="text-[10px] text-amber-700 font-medium leading-tight">Needs re-calibration — the satellite base changed. Click the crosshair to re-mark its two points.</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateLayer(l.id, { visible: !(l.visible !== false) })} title="Show/hide" data-testid={`gis-toggle-${l.id}`}>
                      {l.visible !== false ? <Eye size={15} /> : <EyeSlash size={15} className="text-muted-foreground" />}
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setCalibrate(l)} title="Calibrate points" data-testid={`gis-calibrate-${l.id}`}><Crosshair size={15} className={ok ? "text-primary" : "text-amber-600"} /></Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`gis-up-${l.id}`}><ArrowUp size={15} /></Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === layers.length - 1} onClick={() => move(i, 1)} data-testid={`gis-down-${l.id}`}><ArrowDown size={15} /></Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeLayer(l.id)} data-testid={`gis-del-${l.id}`}><Trash size={15} /></Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground w-12">Opacity</span>
                    <input type="range" min={0.1} max={1} step={0.05} value={l.opacity ?? 0.75} onChange={(e) => updateLayer(l.id, { opacity: parseFloat(e.target.value) })} className="flex-1 accent-[#0A2540]" data-testid={`gis-opacity-${l.id}`} />
                    <span className="text-[10px] tabular w-8 text-right">{Math.round((l.opacity ?? 0.75) * 100)}%</span>
                  </div>
                  {ok && (
                    <div className="space-y-1.5 border-t border-dashed pt-2" data-testid={`gis-adjust-${l.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-12">Rotate</span>
                        <input type="range" min={-180} max={180} step={0.5} value={(l.adjust?.rot) ?? 0} onChange={(e) => setAdj(l, "rot", parseFloat(e.target.value))} className="flex-1 accent-accent" data-testid={`gis-rotate-${l.id}`} />
                        <input type="number" step={0.5} value={(l.adjust?.rot) ?? 0} onChange={(e) => setAdj(l, "rot", parseFloat(e.target.value) || 0)} className="w-12 h-6 text-[10px] border rounded px-1 text-right" data-testid={`gis-rotate-num-${l.id}`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground w-12">Zoom</span>
                        <input type="range" min={0.3} max={3} step={0.02} value={(l.adjust?.zoom) ?? 1} onChange={(e) => setAdj(l, "zoom", parseFloat(e.target.value))} className="flex-1 accent-accent" data-testid={`gis-zoom-${l.id}`} />
                        <span className="text-[10px] w-8 text-right">{Math.round(((l.adjust?.zoom) ?? 1) * 100)}%</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground w-12">Nudge</span>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(l, "dx", ((l.adjust?.dx) ?? 0) - 10)} data-testid={`gis-nudge-left-${l.id}`}><ArrowLeft size={12} /></Button>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(l, "dx", ((l.adjust?.dx) ?? 0) + 10)} data-testid={`gis-nudge-right-${l.id}`}><ArrowRight size={12} /></Button>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(l, "dy", ((l.adjust?.dy) ?? 0) - 10)} data-testid={`gis-nudge-up-${l.id}`}><ArrowUp size={12} /></Button>
                        <Button type="button" size="icon" variant="outline" className="h-6 w-6" onClick={() => setAdj(l, "dy", ((l.adjust?.dy) ?? 0) + 10)} data-testid={`gis-nudge-down-${l.id}`}><ArrowDown size={12} /></Button>
                        <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] ml-auto" onClick={() => updateLayer(l.id, { adjust: { rot: 0, zoom: 1, dx: 0, dy: 0 } })} data-testid={`gis-adjust-reset-${l.id}`}>Reset</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {measurements.length > 0 && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="gis-measure-panel">
              <Label className="text-xs">Measured lines (name each, e.g. “East boundary”)</Label>
              {measurements.map((ml, i) => {
                const len = lengths.find((l) => l.id === (ml.id || i));
                return (
                  <div key={ml.id || i} className="flex items-center gap-1.5" data-testid={`gis-measure-row-${i}`}>
                    <Input value={ml.label || ""} placeholder={`Line ${i + 1}`} onChange={(e) => updateMeasurement(i, { label: e.target.value })} className="h-8 text-xs" data-testid={`gis-measure-label-${i}`} />
                    <span className="text-[11px] text-primary font-medium whitespace-nowrap">{len ? `${len.m.toFixed(1)} m` : "—"}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeMeasurement(i)} data-testid={`gis-measure-del-${i}`}><Trash size={14} /></Button>
                  </div>
                );
              })}
            </div>
          )}

          <ComparablesRadar comparables={comparables} />
        </div>
      </div>

      {calibrate && (
        <CalibrateDialog layer={calibrate}
          refLayer={(baseRef && baseRef.id !== calibrate.id ? baseRef : null) || layers.find((l) => l.id !== calibrate.id && l.points?.a && l.points?.b) || null}
          onClose={() => setCalibrate(null)}
          onSave={(sheetPts, basePts) => {
            const refL = (baseRef && baseRef.id !== calibrate.id ? baseRef : null) || layers.find((l) => l.id !== calibrate.id && l.points?.a && l.points?.b) || null;
            let newLayers = layers.map((l) => (l.id === calibrate.id ? { ...l, points: sheetPts, needs_recalibration: false } : l));
            const upd = {};
            const fitToStage = (nw, nh, mul) => {
              const s = Math.min(STAGE_W / nw, STAGE_H / nh) * mul;
              const tx = STAGE_W / 2 - s * (nw / 2), ty = STAGE_H / 2 - s * (nh / 2);
              return (p) => [Math.round(s * p[0] + tx), Math.round(s * p[1] + ty)];
            };
            if (basePts && refL) {
              // Pair clicks on the base define the shared reference frame (base fit upright to the stage).
              const map = fitToStage(refL.natural_w || STAGE_W, refL.natural_h || STAGE_H, 0.92);
              upd.target = { a: map(basePts.a), b: map(basePts.b) };
              newLayers = newLayers.map((l) => (l.id === refL.id ? { ...l, points: basePts, auto: false, needs_recalibration: false } : l));
              // The georeferenced base knows its metres-per-pixel, so the two known points set the ground scale automatically.
              if (refL.geo?.mpp) {
                const dm = Math.round(Math.hypot(basePts.b[0] - basePts.a[0], basePts.b[1] - basePts.a[1]) * refL.geo.mpp);
                if (dm > 0) upd.real_distance_m = dm;
              }
            } else if (!refL) {
              // No base at all: this sheet becomes the reference (fit upright to the stage).
              const map = fitToStage(calibrate.natural_w || STAGE_W, calibrate.natural_h || STAGE_H, 0.9);
              upd.target = { a: map(sheetPts.a), b: map(sheetPts.b) };
            }
            upd.layers = newLayers;
            patch(upd);
            setCalibrate(null);
            toast.success("Snapped to the base — the 2 landmarks now line up. Fine-tune with the layer's Rotate / Nudge if needed.");
          }} />
      )}
    </div>
  );
}
